import { openZendeskChat } from '@/components/ZendeskChat/ZendeskChat';
import type { IAiSearchResponse } from '@/services/aiSearch/client';
import { trackEvent } from '@/services/track-event';

/**
 * Shown when an AI-search query isn't a search: a policy/how-it-works
 * question (outcome 'question', with real help-center articles the server
 * looked up) or a request to reach a person (outcome 'handoff'). Never
 * touches the filters — the box just points the renter at the right place.
 */
export default function AiSearchHelpPanel({
  resp,
  surface,
  onDismiss,
}: {
  resp: IAiSearchResponse;
  surface: string;
  onDismiss: () => void;
}) {
  const isHandoff = resp.outcome === 'handoff';
  const articles = resp.help?.articles ?? [];

  const chatWithSupport = () => {
    trackEvent({ event: 'AI Search Support Requested', surface, outcome: resp.outcome });
    openZendeskChat();
  };

  return (
    <div className="p-4 text-left bg-white border shadow-lg rounded-2xl border-neutral-30">
      <div className="flex items-start justify-between gap-3">
        <p className="text-base font-medium text-gray-900">
          {isHandoff ? 'Looking to talk to a person?' : "That's a question, not a search."}
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="text-gray-400 shrink-0 hover:text-gray-700">
          ✕
        </button>
      </div>

      {!isHandoff && articles.length > 0 && (
        <>
          <p className="mt-1 text-sm text-gray-500">This might help:</p>
          <ul className="mt-2 space-y-1">
            {articles.map(article => (
              <li key={article.url}>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent({
                      event: 'AI Search Help Article Clicked',
                      surface,
                      url: article.url,
                      title: article.title,
                    })
                  }
                  className="block px-3 py-2 rounded-lg hover:bg-[#f1ebdc]">
                  <span className="text-base text-gray-900 underline">{article.title}</span>
                  {article.snippet && (
                    <span className="block text-sm text-gray-500 mt-0.5 line-clamp-2">
                      {article.snippet}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      {!isHandoff && articles.length === 0 && (
        <p className="mt-1 text-sm text-gray-500">
          I can only search for RVs here. Try the Help Center, or chat with our support team.
        </p>
      )}

      <div className="flex flex-wrap items-center mt-3 gap-3">
        <button
          type="button"
          onClick={chatWithSupport}
          className="px-4 py-2 text-base font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800">
          Chat with support
        </button>
        {!isHandoff && resp.help?.searchUrl && (
          <a
            href={resp.help.searchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base text-gray-700 underline hover:text-gray-900">
            Search the Help Center
          </a>
        )}
      </div>
    </div>
  );
}
